import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View, FlatList, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Text } from '../../../components/ui/AppText';
import { useInfiniteVideos, selectVideoItems } from '../hooks/useInfiniteVideos';
import { useUserVideoLikes } from '../hooks/useVideoMutations';
import { videoToFeedPost } from '../services/communityVideosService';
import { VIDEO_SORT, VIDEO_SECTION } from '../constants';
import VideoCard from './VideoCard';
import VideoComposerModal from './VideoComposerModal';
import { styles, PLAYER_SLOT_MARGIN_BOTTOM } from './styles';

/**
 * "Mais relevantes" — coleção própria (section='relevant'), ordenada por votos.
 * Postagem AQUI é só YouTube (allowGallery=false). Não compartilha vídeos com "Atualizações".
 *
 * ── Reprodução ────────────────────────────────────────────────────────────────
 * Esta seção NÃO tem player próprio: ela reusa o player ÚNICO da tela
 * (`FeedVideoOverlay`), o mesmo de "Posts em Alta". Aqui fica só o espaço
 * reservado 16:9 sobre o qual esse player se ancora — exatamente o contrato do
 * `PostCard` (ver o bloco de vídeo lá). É o que dá largura/altura/posição
 * idênticas às do feed e, de brinde, a miniatura ao rolar sem cortar o áudio.
 *
 * "Atualizações" continua com o `VideoPlayerModal` (reel de tela cheia) — as duas
 * seções deixaram de compartilhar o modo de reprodução de propósito.
 */
const VideoFeedSection = ({
  colors,
  isAuthenticated,
  onRequireLogin,
  activeVideoId,
  onRequestPlayVideo,
  onMeasureVideoSlot,
  onRegisterVideoMeasure,
  onReleaseVideoSlot,
}) => {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteVideos({ sort: VIDEO_SORT.RELEVANT, section: VIDEO_SECTION.RELEVANT });
  const { data: likedIds = [] } = useUserVideoLikes(!!isAuthenticated);
  const [composerVisible, setComposerVisible] = useState(false);

  const videos = selectVideoItems(data);
  const likedSet = useMemo(() => new Set(likedIds), [likedIds]);

  // O player da tela é um só: o vídeo ativo pode ser um post do feed. A seção só
  // reserva espaço quando o ativo é DELA.
  const isPlayingHere = !!activeVideoId && videos.some((v) => v.id === activeVideoId);

  // O que está tocando sai do carrossel — ele já está em cima, no player grande.
  const carouselVideos = useMemo(
    () => (isPlayingHere ? videos.filter((v) => v.id !== activeVideoId) : videos),
    [videos, isPlayingHere, activeVideoId]
  );

  // ── Espaço reservado + medição (mesmo contrato do PostCard) ────────────────
  const slotRef = useRef(null);
  const slotHeightRef = useRef(0);

  const reportSlot = useCallback(() => {
    slotRef.current?.measureInWindow?.((x, y) => {
      if (typeof y === 'number') onMeasureVideoSlot?.(y);
    });
  }, [onMeasureVideoSlot]);

  const handleSlotLayout = useCallback((event) => {
    const height = event?.nativeEvent?.layout?.height;
    if (height > 0) slotHeightRef.current = height;
    reportSlot();
  }, [reportSlot]);

  // ── Devolução do espaço ────────────────────────────────────────────────────
  // O slot vive no CABEÇALHO da lista. Quando o player passa para um post do
  // feed, ele desmonta e ~231px somem ACIMA do que o usuário está olhando —
  // todo o conteúdo abaixo sobe de uma vez. O `maintainVisibleContentPosition`
  // da FlashList (ligado por padrão na New Arch) não cobre este caso, porque o
  // cabeçalho não é um item indexado da lista.
  //
  // `useLayoutEffect` e não `useEffect`: a compensação precisa sair no mesmo
  // ciclo da remoção. Num efeito comum o usuário veria o salto e só então a
  // correção — dois movimentos em vez de nenhum.
  const wasPlayingHereRef = useRef(false);
  useLayoutEffect(() => {
    if (wasPlayingHereRef.current && !isPlayingHere) {
      const released = slotHeightRef.current + PLAYER_SLOT_MARGIN_BOTTOM;
      if (released > 0) onReleaseVideoSlot?.(released);
    }
    wasPlayingHereRef.current = isPlayingHere;
  }, [isPlayingHere, onReleaseVideoSlot]);

  // Enquanto o vídeo ativo é desta seção, ela empresta seu medidor para a tela
  // (a lista corrige alturas depois de montar, e a tela precisa remedir).
  useEffect(() => {
    if (!isPlayingHere) return undefined;

    reportSlot();
    onRegisterVideoMeasure?.(reportSlot);
    return () => onRegisterVideoMeasure?.(null);
  }, [isPlayingHere, reportSlot, onRegisterVideoMeasure]);

  // A reprodução segue a MESMA lógica de "Posts em Alta": o player toca e, ao
  // terminar, fecha. (Houve uma versão com avanço automático para o próximo do
  // carrossel; foi removida em 2026-08-08 a pedido, para as duas seções se
  // comportarem igual — ver decision-log.)
  //
  // `isSectionVideo` marca para a tela que este vídeo NÃO tem post por trás: é o
  // que impede o toque na miniatura de abrir a folha de detalhe.
  const playVideo = useCallback((video) => {
    const feedPost = videoToFeedPost(video);
    if (!feedPost) return;
    onRequestPlayVideo?.(feedPost, { isSectionVideo: true });
  }, [onRequestPlayVideo]);

  const renderItem = useCallback(
    ({ item }) => (
      <VideoCard video={item} liked={likedSet.has(item.id)} colors={colors} onPress={playVideo} />
    ),
    [likedSet, colors, playVideo]
  );

  const handleEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleAdd = useCallback(() => {
    if (!isAuthenticated) { onRequireLogin?.(); return; }
    setComposerVisible(true);
  }, [isAuthenticated, onRequireLogin]);

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text style={[styles.sectionTitle, { color: colors?.textPrimary }]}>Mais relevantes</Text>
        <TouchableOpacity style={styles.addButton} onPress={handleAdd} hitSlop={8}>
          <Text style={styles.addButtonText}>+ Vídeo</Text>
        </TouchableOpacity>
      </View>

      {/* Espaço reservado: o player real é o FeedVideoOverlay da tela, que se
          posiciona sobre este retângulo. `collapsable={false}` é obrigatório —
          sem ele o Android pode achatar a View e `measureInWindow` não responde. */}
      {isPlayingHere ? (
        <View
          ref={slotRef}
          onLayout={handleSlotLayout}
          collapsable={false}
          style={styles.playerSlot}
        />
      ) : null}

      {isLoading ? (
        <ActivityIndicator style={styles.loader} />
      ) : videos.length === 0 ? (
        <Text style={styles.emptyText}>Nenhum vídeo ainda. Toque em "+ Vídeo" para adicionar um do YouTube.</Text>
      ) : carouselVideos.length === 0 ? (
        // Único vídeo da seção, e é ele que está tocando: sem carrossel e SEM o
        // texto de vazio — dizer "nenhum vídeo ainda" com um tocando seria falso.
        null
      ) : (
        <FlatList
          horizontal
          data={carouselVideos}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          extraData={activeVideoId}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.hScroll}
          onEndReached={handleEnd}
          onEndReachedThreshold={0.5}
        />
      )}

      <VideoComposerModal
        visible={composerVisible}
        onClose={() => setComposerVisible(false)}
        section={VIDEO_SECTION.RELEVANT}
        allowGallery={false}
      />
    </View>
  );
};

export default React.memo(VideoFeedSection);
